
import { useEffect, useRef, useState } from "react";
import { QuoteIcon } from "lucide-react"

import { cn } from "../../lib/utils"
import { Swiper, SwiperSlide } from "swiper/react"
import { Autoplay, Pagination } from "swiper/modules"

import "swiper/css"
import "swiper/css/pagination"

/** Real customer logo; falls back to the company name if the file can't load. */
function Logo({ name, src, className }) {
  const [failed, setFailed] = useState(false)

  if (failed) {
    return (
      <span className={cn("text-sm font-semibold text-ink-strong", className)}>
        {name}
      </span>
    )
  }

  return (
    <img
      src={src}
      alt={`${name} | Onbbits`}
      loading="lazy"
      height={40}
      onError={() => setFailed(true)}
      className={cn("h-[80px] w-auto max-w-[150px] object-contain", className)}
    />
  )
}

/** "Trusted by" strip directly under the hero. */
export function LogoStrip({ label, logos }) {
  return (
    <section aria-label={label} className="border-b border-border bg-background py-8">
      <div className="mx-auto flex w-full max-w-[1140px] flex-col items-center gap-5 px-4 sm:px-6 md:flex-row md:gap-10">
        <p className="shrink-0 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          {label}
        </p>

        <ul className="flex flex-1 flex-wrap items-center justify-center gap-x-10 gap-y-5 md:justify-between">
          {logos.map((l) => (
            <li
              key={l.name}
              className="opacity-80 grayscale transition hover:opacity-100 hover:grayscale-0"
            >
              <Logo {...l} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

/** Result cards: customer-reported metric, quote and customer details. */
export function ResultCards({ items, footnote }) {
  return (
    <div className="flex flex-col gap-4">
      <ul className="grid gap-4 lg:grid-cols-3">
        {items.map((r, i) => {
          const featured = i === 0

          return (
            <li
              key={r.name}
              className={cn(
                "flex flex-col gap-5 rounded-xl border p-6 shadow-card",
                featured
                  ? "border-transparent bg-charcoal text-on-dark"
                  : "border-border bg-background"
              )}
            >
              <p className="flex items-baseline gap-2">
                <span
                  className={cn(
                    "text-5xl font-semibold tracking-tight",
                    featured ? "text-brand-bright" : "text-brand-text"
                  )}
                >
                  {r.metric}
                </span>

                <span
                  className={cn(
                    "text-sm font-medium",
                    featured ? "text-on-dark" : "text-ink-strong"
                  )}
                >
                  {r.label}
                </span>
              </p>

              <blockquote className="flex flex-1 gap-2">
                <QuoteIcon
                  className={cn(
                    "mt-0.5 size-4 shrink-0",
                    featured ? "text-brand-bright" : "text-brand"
                  )}
                  aria-hidden="true"
                />

                <p
                  className={cn(
                    "text-[15px] leading-relaxed font-light",
                    featured ? "text-on-dark" : "text-ink-strong"
                  )}
                >
                  {r.quote}
                </p>
              </blockquote>

              <footer
                className={cn(
                  "flex items-center justify-between gap-3 border-t pt-4",
                  featured ? "border-white/10" : "border-border"
                )}
              >
                <p className="text-sm">
                  <span className="block font-semibold">{r.name}</span>
                  <span className={featured ? "text-on-dark-muted" : "text-muted-foreground"}>
                    {r.role}
                  </span>
                </p>

                {r.logo && (
                  <span className={cn("rounded-md p-1.5", featured && "bg-white")}>
                    <Logo
                      name={r.role.split(", ").pop() ?? r.name}
                      src={r.logo}
                      className="h-7 sm:h-7"
                    />
                  </span>
                )}
              </footer>
            </li>
          )
        })}
      </ul>

      <p className="text-xs text-muted-foreground">{footnote}</p>
    </div>
  )
}

/** One editorial quote matched to the visitor's chosen job. */
export function FeaturedQuote({ item, lead, footnote }) {
  return (
    <figure className="animate-node-in flex flex-col gap-6 rounded-2xl border border-border bg-background p-6 shadow-card-float sm:p-10">
      {lead && (
        <p className="text-sm font-semibold text-ink-strong">{lead}</p>
      )}

      <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-6xl font-semibold tracking-tight text-brand-text sm:text-7xl">
          {item.metric}
        </span>
        <span className="text-lg font-medium text-ink-strong">
          {item.label}
        </span>
      </p>

      <blockquote className="flex gap-3">
        <QuoteIcon
          className="mt-1 size-6 shrink-0 text-brand"
          aria-hidden="true"
        />
        <p className="text-xl leading-relaxed font-light text-balance text-foreground sm:text-2xl">
          {item.quote}
        </p>
      </blockquote>

      <figcaption className="flex flex-wrap items-center justify-between gap-4 border-t border-border pt-5">
        <p className="text-sm">
          <span className="block font-semibold">{item.name}</span>
          <span className="text-muted-foreground">{item.role}</span>
        </p>

        {item.logo && (
          <Logo
            name={item.role.split(", ").pop() ?? item.name}
            src={item.logo}
            className="h-8 sm:h-9"
          />
        )}
      </figcaption>

      <p className="text-xs text-muted-foreground">{footnote}</p>
    </figure>
  )
}

export function TestimonialCarousel({
  items,
  lead,
  footnote,
  targetIndex = 0,
  jumpTrigger,
}) {
  const swiperRef = useRef(null);

  useEffect(() => {
    const swiper = swiperRef.current;

    if (!swiper || swiper.destroyed) return;

    swiper.slideToLoop(targetIndex, 0);
  }, [targetIndex, jumpTrigger]);

  if (!items.length) return null;

  return (
    <Swiper
      onSwiper={(swiper) => {
        swiperRef.current = swiper;
        swiper.slideToLoop(targetIndex, 0);
      }}
      modules={[Autoplay, Pagination]}
      slidesPerView={1}
      spaceBetween={20}
      loop={items.length > 1}
      autoplay={
        items.length > 1
          ? {
              delay: 4500,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }
          : false
      }
      pagination={{ clickable: true }}
      className="testimonial-swiper"
    >
      {items.map((item, index) => (
        <SwiperSlide key={`${item.name}-${index}`} className="pb-10">
          <FeaturedQuote
            item={item}
            lead={index === 0 ? lead : undefined}
            footnote={footnote}
          />
        </SwiperSlide>
      ))}
    </Swiper>
  );
}